import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";

import Button from "../../components/Button";
import PasswordField from "../../components/PasswordField";
import SecurityQuestionsForm from "../../components/SetSecurityQuestion/index";

import useDocumentTitle from "../../utils/useDocumentTitle";

import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import getUserIP, { formatIPJSON } from "../../utils/getUserIP";
import styles from "./Settings.module.scss";

export default function ChangeSecurityQuestion(props) {
  const { t } = useTranslation();
  useDocumentTitle(props.title);

  const history = useHistory();
  const { addNotification } = useNotification();

  const [password, setPassword] = useState("");
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [answers, setAnswers] = useState([]);
  const [isValid, setIsValid] = useState(false);

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);


  // VERIFY PASSWORD
  const handleVerifyPassword = async (e) => {
    if (e) e.preventDefault();

    if (!password) {
      addNotification(t("changeSecurityQuestion.verifyPassword"), "error");
      return;
    }

    try {
      setVerifying(true);
      const res = await apiRequest.post("/api/verify-password", {
        uid: localStorage.getItem("uid"),
        password: password,
      });

      if (res.data.status === "success") {
        setPasswordVerified(true);
        addNotification(t("changeSecurityQuestion.passwordVerified"), "success");
      } else {
        addNotification(res.data.msg || t("changeSecurityQuestion.invalidPassword"), "error");
      }
    } catch (err) {
      addNotification(
        err.response?.data?.msg || t("changeSecurityQuestion.failedVerify"),
        "error"
      );
    } finally {
      setVerifying(false);
    }
  };

  // SUBMIT SECURITY QUESTIONS
  const handleSubmit = async () => {
    if (!passwordVerified) {
      addNotification(t("changeSecurityQuestion.pleaseVerifyFirst"), "error");
      return;
    }

    if (!isValid) {
      addNotification(t("changeSecurityQuestion.pleaseComplete"), "error");
      return;
    }

    try {
      setLoading(true);
      const userIp = await getUserIP();
      const res = await apiRequest.post("/api/security-questions", {
        answers,
        userIp: formatIPJSON(userIp)
      });

      // only treat as success if HTTP 200
      if (res.status === 200) {
        addNotification(
          res.data?.msg || t("changeSecurityQuestion.updated"),
          "success"
        );

        setTimeout(() => {
          history.go(0);
        }, 800);
      } else {
        addNotification(
          res.data?.msg || t("changeSecurityQuestion.failedUpdate"),
          "error"
        );
      }
    } catch (err) {
      addNotification(
        err.response?.data?.msg || t("changeSecurityQuestion.failedUpdate"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = !isValid || loading || !passwordVerified;

  // ENTER KEY
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleVerifyPassword();
    }
  };

  return (
    <DashboardLayout>
      <div className={styles.pageWrapper}>
        <PageContent>
          <Section>

            <SectionTitle style={{ marginBottom: "-30px" }}>
              {t("changeSecurityQuestion.title")}
            </SectionTitle>

            <div className={styles.detailsContainer}>
              <div className="d-flex">

                <form
                  className={styles.passwordSection}
                  onSubmit={handleVerifyPassword}
                >

                  {!passwordVerified && (
                    <div style={{ marginBottom: "24px" }}>

                      <div
                        className="grey-600 text-body-1"
                        style={{ marginBottom: "36px" }}
                      >
                        {t("changeSecurityQuestion.verifyIdentity")}
                      </div>

                      <div style={{ marginBottom: "20px" }}>
                        <PasswordField
                          autoComplete="current-password"
                          label={t("changeSecurityQuestion.verifyPassword")}
                          value={password}
                          show={showPassword}
                          onChange={(e) => setPassword(e.target.value)}
                          onKeyDown={handleKeyDown}
                          onShowClick={() =>
                            setShowPassword((prev) => !prev)
                          }
                          onShowMouseDown={(e) => e.preventDefault()}
                          className={styles.passwordField}
                        />
                      </div>

                      <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        fullWidth
                        isloading={verifying ? 1 : 0}
                        disabled={!password || verifying}
                      >
                        {t("changeSecurityQuestion.continue")}
                      </Button>
                    </div>
                  )}

                  {passwordVerified && (
                    <>
                      <div
                        className="grey-600 text-body-1"
                        style={{ marginBottom: "36px" }}
                      >
                        {t("changeSecurityQuestion.differentQuestions")}
                      </div>

                      <SecurityQuestionsForm
                        onChange={setAnswers}
                        onValidityChange={setIsValid}
                      />

                      <Button
                        type="button"
                        color="primary"
                        variant="contained"
                        fullWidth
                        isloading={loading ? 1 : 0}
                        disabled={isDisabled}
                        onClick={handleSubmit}
                      >
                        {t("changeSecurityQuestion.save")}
                      </Button>
                    </>
                  )}

                </form>

              </div>
            </div>

          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}